import React, { useState, useEffect, useCallback } from 'react';
import {
  Clock, CheckCircle, XCircle, Loader2, RefreshCw,
  PlayCircle, StopCircle, RotateCcw, DollarSign
} from 'lucide-react';
import { API_URL } from '../../config';

// ============================================================
// ADMIN - Essai gratuit 7 jours (Messenger Bot)
// Un client ne peut avoir qu'un seul essai a la fois (trial_used) ; l'admin peut le
// reinitialiser explicitement via "Reinitialiser l'essai" si besoin (client saisonnier,
// erreur de manipulation, etc.) - trace via trial_reset_at/trial_reset_by.
// ============================================================

const authHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
  'Content-Type': 'application/json'
});

const FILTERS = [
  { key: 'eligible', label: 'Éligibles' },
  { key: 'active', label: 'Actifs' },
  { key: 'expired', label: 'Expirés' },
  { key: 'ended_early', label: 'Arrêtés' },
  { key: 'converted', label: 'Convertis' },
];

const STATUS_BADGES = {
  none: { label: 'Jamais utilisé', color: 'bg-gray-500/20 text-gray-300' },
  active: { label: 'Essai actif', color: 'bg-blue-500/20 text-blue-300' },
  expired: { label: 'Expiré', color: 'bg-red-500/20 text-red-300' },
  ended_early: { label: 'Arrêté (admin)', color: 'bg-orange-500/20 text-orange-300' },
  converted: { label: 'Converti ✓', color: 'bg-green-500/20 text-green-300' },
};

function daysLeft(endsAt) {
  if (!endsAt) return null;
  const ms = new Date(endsAt).getTime() - Date.now();
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}

export default function TrialManagement() {
  const [filter, setFilter] = useState('eligible');
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [message, setMessage] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/trials?filter=${filter}`, {
        headers: authHeaders()
      });
      const data = await res.json();
      if (data.success) setClients(data.clients);
    } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const runAction = async (client, action, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusyId(client.$id);
    setMessage(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/trials/${client.$id}/${action}`, {
        method: 'POST', headers: authHeaders()
      });
      const data = await res.json();
      setMessage({ type: data.success ? 'ok' : 'error', text: data.message });
      load();
    } finally { setBusyId(null); }
  };

  const startTrial = (c) => runAction(c, 'start');
  const endTrial = (c) => runAction(c, 'end', `Arrêter l'essai de "${c.facebook_page_name || c.name}" maintenant ? Le bot sera désactivé immédiatement.`);
  const convertTrial = (c) => runAction(c, 'convert', `Marquer "${c.facebook_page_name || c.name}" comme client payant (converti) ?`);
  const resetTrial = (c) => runAction(c, 'reset-trial', `Réinitialiser l'essai de "${c.facebook_page_name || c.name}" ? Il pourra en redémarrer un nouveau.`);

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-1 flex items-center gap-3">
            <Clock className="w-8 h-8 text-blue-400" />
            Essai Gratuit 7 jours
          </h1>
          <p className="text-sm text-blue-200">
            Démarrez, suivez et convertissez les essais gratuits du bot Messenger — un essai par client, coupure automatique à J+7.
          </p>
        </div>
        <button onClick={() => load()} className="p-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-white self-start">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="flex gap-2 flex-wrap">
        {FILTERS.map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
              filter === f.key ? 'bg-blue-600 text-white' : 'bg-white/10 text-gray-300 hover:bg-white/20'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {message && (
        <div className={`p-4 rounded-2xl flex items-center gap-3 text-sm ${
          message.type === 'ok'
            ? 'bg-green-500/10 border border-green-500/30 text-green-300'
            : 'bg-red-500/10 border border-red-500/30 text-red-300'
        }`}>
          {message.type === 'ok' ? <CheckCircle className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
          {message.text}
        </div>
      )}

      {loading ? (
        <div className="py-16 text-center"><Loader2 className="w-10 h-10 text-blue-400 animate-spin mx-auto" /></div>
      ) : clients.length === 0 ? (
        <div className="bg-white/5 border border-white/10 rounded-2xl p-10 text-center">
          <Clock className="w-12 h-12 text-gray-500/50 mx-auto mb-3" />
          <p className="text-gray-400">Aucun client dans cette catégorie.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {clients.map(client => {
            const st = STATUS_BADGES[client.trial_status || 'none'] || STATUS_BADGES.none;
            const remaining = client.trial_status === 'active' ? daysLeft(client.trial_ends_at) : null;
            const isBusy = busyId === client.$id;
            return (
              <div key={client.$id} className="bg-white/5 border border-white/10 rounded-2xl p-4 md:p-5">
                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <span className="text-white font-semibold text-sm">{client.facebook_page_name || client.name}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${st.color}`}>{st.label}</span>
                      {client.trial_status === 'active' && (
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${client.bot_is_active ? 'bg-green-500/20 text-green-300' : 'bg-red-500/20 text-red-300'}`}>
                          {client.bot_is_active ? 'Bot actif' : 'Bot inactif'}
                        </span>
                      )}
                      {remaining !== null && (
                        <span className="text-yellow-300 text-xs font-medium">
                          {remaining > 0 ? `${remaining} j restant${remaining > 1 ? 's' : ''}` : 'Expire aujourd\'hui'}
                        </span>
                      )}
                    </div>
                    <div className="text-gray-500 text-xs flex flex-wrap gap-x-4">
                      {client.trial_started_at && <span>Démarré : {new Date(client.trial_started_at).toLocaleDateString('fr-FR')}</span>}
                      {client.trial_ends_at && <span>Fin prévue : {new Date(client.trial_ends_at).toLocaleDateString('fr-FR')}</span>}
                      {client.trial_converted_at && <span>Converti le : {new Date(client.trial_converted_at).toLocaleDateString('fr-FR')}</span>}
                    </div>
                  </div>

                  <div className="flex gap-2 flex-wrap flex-shrink-0">
                    {!client.trial_used && (
                      <button
                        onClick={() => startTrial(client)}
                        disabled={isBusy}
                        className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-medium text-sm transition-all disabled:opacity-50"
                      >
                        {isBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
                        Démarrer l'essai
                      </button>
                    )}
                    {client.trial_status === 'active' && (
                      <button
                        onClick={() => endTrial(client)}
                        disabled={isBusy}
                        className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-red-500/20 text-red-300 hover:bg-red-500/30 font-medium text-sm transition-all disabled:opacity-50"
                      >
                        <StopCircle className="w-4 h-4" /> Arrêter maintenant
                      </button>
                    )}
                    {(client.trial_status === 'active' || client.trial_status === 'expired' || client.trial_status === 'ended_early') && (
                      <button
                        onClick={() => convertTrial(client)}
                        disabled={isBusy}
                        className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-green-600 hover:bg-green-500 text-white font-medium text-sm transition-all disabled:opacity-50"
                      >
                        <DollarSign className="w-4 h-4" /> Marquer converti
                      </button>
                    )}
                    {(client.trial_status === 'expired' || client.trial_status === 'ended_early') && (
                      <button
                        onClick={() => resetTrial(client)}
                        disabled={isBusy}
                        className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/10 text-gray-300 hover:bg-white/20 font-medium text-sm transition-all disabled:opacity-50"
                      >
                        <RotateCcw className="w-4 h-4" /> Réinitialiser l'essai
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
